import {ContractType, JobRole} from "@/core/interfaces/TeamPlanning";
import LegacyAccess from "@/core/interfaces/Access";
import {PATIENT_RANK_ID} from "@/agenda/constants";
import {ShiftInput} from "@/core/http/resources/TeamPlanningResource";

export interface JobRoleOption {
    value: JobRole;
    label: string;
    color: string;
}

/**
 * The business roles, each carrying the planning color of the mockup.
 */
export const JOB_ROLES: JobRoleOption[] = [
    {value: 'pharmacist', label: 'Pharmacien', color: '#3513e2'},
    {value: 'preparer', label: 'Préparateur', color: '#5dcfff'},
    {value: 'esthetician', label: 'Esthéticienne', color: '#ff80a2'},
    {value: 'apprentice', label: 'Apprenti', color: '#c9a6f0'},
    {value: 'shelf_stocker', label: 'Rayonniste', color: '#b6ac9a'}
];

export const CONTRACT_TYPES: { value: ContractType; label: string }[] = [
    {value: 'permanent', label: 'CDI'},
    {value: 'fixed_term', label: 'CDD'},
    {value: 'interim', label: 'Intérim'},
    {value: 'internship', label: 'Stagiaire'},
    {value: 'work_study', label: 'Alternance'}
];

/** Carbon convention: index 0 is Sunday. */
export const DAY_LABELS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export const DAY_SHORT_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

/** Days in display order, Monday first. */
export const DISPLAY_DAYS = [1, 2, 3, 4, 5, 6, 0];

export const WEEK_LABELS = ['Semaine A', 'Semaine B', 'Semaine C', 'Semaine D'];

/**
 * Display name of a rotation week. No parity naming: the cycle runs on across
 * years, so week A matches even ISO weeks in 2026 only.
 */
export const weekLabel = (weekIndex: number): string => WEEK_LABELS[weekIndex];

export const ROTATION_PRESETS = [
    {weeks: 1, label: 'Hebdomadaire'},
    {weeks: 2, label: 'Semaines A / B'},
    {weeks: 3, label: 'Cycle 3 semaines'},
    {weeks: 4, label: 'Cycle 4 semaines'}
];

export interface TimeRange {
    starts_at: string;
    ends_at: string;
}

/** Editable grid: ranges[weekIndex][dayOfWeek] = time ranges of the day. */
export type ScheduleGrid = TimeRange[][][];

export const jobRoleOption = (role: JobRole | null): JobRoleOption | undefined =>
    JOB_ROLES.find(option => option.value === role);

/**
 * The planning role — and its color — derives from the access rank: both
 * describe the same job, the rank being the single field the user edits.
 * Keys are User_Access_Rank ids.
 */
const JOB_ROLE_BY_RANK_ID: Record<number, JobRole> = {
    1: 'pharmacist',   // titulaire
    2: 'pharmacist',   // co-titulaire
    3: 'preparer',     // préparateur
    6: 'pharmacist',   // pharmacien assistant
    7: 'esthetician',  // esthéticienne
    8: 'shelf_stocker', // rayonniste
    9: 'apprentice',   // apprenti
    10: 'apprentice',  // étudiant en pharmacie
    11: 'pharmacist'   // pharmacien adhérent
};

export const jobRoleForRank = (rankId?: number | null): JobRole | null =>
    rankId ? JOB_ROLE_BY_RANK_ID[rankId] ?? null : null;

/** Fallback color for a member whose rank maps to no business role. */
export const UNKNOWN_ROLE_COLOR = '#8b93a7';

/** The team's staff: patient-rank accesses share the organization without working there. */
export const staffMembers = (members: LegacyAccess[]): LegacyAccess[] =>
    members.filter(member => member.rankId !== PATIENT_RANK_ID);

/** Shown when the access behind a record is no longer part of the team. */
export const UNKNOWN_MEMBER_NAME = 'Membre retiré de l\'équipe';

export const memberName = (member?: LegacyAccess): string => member?.user?.name ?? UNKNOWN_MEMBER_NAME;

export const initialsOfName = (name: string): string => name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

export const memberInitials = (member?: LegacyAccess): string => initialsOfName(memberName(member));

/** The member's avatar color, taken from their business role. */
export const memberColor = (member?: LegacyAccess): string =>
    JOB_ROLES.find(role => role.value === jobRoleForRank(member?.rankId))?.color ?? UNKNOWN_ROLE_COLOR;

/**
 * Parse the contractual weekly hours typed by the user: null for an emptied
 * field, undefined when the text is not a valid amount — the caller must then
 * refuse the save (a NaN would serialize to null and wipe the stored value).
 */
export const parseWeeklyHours = (text: string): number | null | undefined => {
    const trimmed = text.trim();

    if (trimmed === '') {
        return null;
    }

    const value = Number(trimmed.replace(',', '.'));

    return Number.isFinite(value) && value >= 0 && value <= 80 ? value : undefined;
};

/** Ranks whose holders must carry a RPPS number: titulaire, co-titulaire, pharmacien assistant. */
const RPPS_RANK_IDS = [1, 2, 6];

export const rankRequiresRpps = (rankId?: number | null): boolean =>
    !!rankId && RPPS_RANK_IDS.includes(rankId);

export const contractLabel = (contract: ContractType | null): string | undefined =>
    CONTRACT_TYPES.find(option => option.value === contract)?.label;

export const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);

    return hours * 60 + minutes;
};

export const toTime = (minutes: number): string => {
    const clamped = Math.min(Math.max(minutes, 0), 23 * 60 + 45);

    return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
};

/** "9h", "12h30". */
export const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');

    return `${Number(hours)}h${minutes === '00' ? '' : minutes}`;
};

/** "35 h", "24,5 h". */
export const formatHours = (minutes: number): string => {
    const hours = minutes / 60;

    return `${(Math.round(hours * 100) / 100).toLocaleString('fr-FR')} h`;
};

/** "+2,5 h", "−2 h": a signed counter delta or balance, mockup format. */
export const formatSignedHours = (minutes: number): string =>
    `${minutes < 0 ? '−' : '+'}${formatHours(Math.abs(minutes))}`;

export const rangeMinutes = (range: TimeRange): number =>
    Math.max(toMinutes(range.ends_at) - toMinutes(range.starts_at), 0);

/**
 * Build the editable grid from the API shifts.
 */
export const buildGrid = (shifts: ShiftInput[], rotationWeeks: number): ScheduleGrid => {
    const grid: ScheduleGrid = Array.from(
        {length: rotationWeeks},
        () => Array.from({length: 7}, () => [] as TimeRange[])
    );

    shifts.forEach(shift => {
        if (shift.week_index < rotationWeeks) {
            grid[shift.week_index][shift.day_of_week].push({starts_at: shift.starts_at, ends_at: shift.ends_at});
        }
    });

    grid.forEach(week => week.forEach(day => day.sort((a, b) => toMinutes(a.starts_at) - toMinutes(b.starts_at))));

    return grid;
};

/**
 * Flatten the editable grid back to the sync payload.
 */
export const gridToShifts = (grid: ScheduleGrid): ShiftInput[] =>
    grid.flatMap((week, weekIndex) =>
        week.flatMap((day, dayOfWeek) =>
            day.map(range => ({
                week_index: weekIndex,
                day_of_week: dayOfWeek,
                starts_at: range.starts_at,
                ends_at: range.ends_at
            }))
        )
    );

export const weekMinutes = (week: TimeRange[][]): number =>
    week.reduce((total, day) => total + day.reduce((sum, range) => sum + rangeMinutes(range), 0), 0);

/**
 * Average worked hours per week over the whole rotation.
 */
export const averageWeeklyMinutes = (grid: ScheduleGrid): number => {
    if (!grid.length) {
        return 0;
    }

    return grid.reduce((total, week) => total + weekMinutes(week), 0) / grid.length;
};

const formatRanges = (ranges: TimeRange[]): string =>
    ranges.map(range => `${formatTime(range.starts_at)}–${formatTime(range.ends_at)}`).join(' et ');

/**
 * Human summary of the whole recurrence, consecutive days with identical
 * ranges being grouped ("Lun–Ven 9h–12h30 et 14h–19h").
 */
export const buildScheduleSentence = (grid: ScheduleGrid): string => {
    if (!grid.some(week => week.some(day => day.length))) {
        return 'Aucun horaire récurrent défini pour le moment.';
    }

    const weeks = grid.map((week, weekIndex) => {
        const groups: { days: number[]; ranges: TimeRange[] }[] = [];

        DISPLAY_DAYS.forEach(day => {
            const ranges = week[day];

            if (!ranges.length) {
                return;
            }

            const last = groups[groups.length - 1];
            const sameAsLast = last
                && formatRanges(last.ranges) === formatRanges(ranges)
                && DISPLAY_DAYS.indexOf(last.days[last.days.length - 1]) === DISPLAY_DAYS.indexOf(day) - 1;

            if (sameAsLast) {
                last.days.push(day);
            } else {
                groups.push({days: [day], ranges});
            }
        });

        if (!groups.length) {
            return null;
        }

        const parts = groups.map(group => {
            const days = group.days.length > 2
                ? `${DAY_SHORT_LABELS[group.days[0]]}–${DAY_SHORT_LABELS[group.days[group.days.length - 1]]}`
                : group.days.map(day => DAY_SHORT_LABELS[day]).join(', ');

            return `${days} ${formatRanges(group.ranges)}`;
        });

        const prefix = grid.length > 1 ? `${weekLabel(weekIndex)} : ` : '';

        return `${prefix}${parts.join(' ; ')} — ${formatHours(weekMinutes(week))}`;
    }).filter(Boolean);

    return `${weeks.join('. ')}.`;
};
